import React, { useState, useEffect, useCallback, useRef } from 'react';
import { DataChange } from 'devextreme/common/grids';
import Tabs, { Item } from 'devextreme-react/tabs';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { Toast } from 'devextreme-react/toast';
import { formatMessage } from 'devextreme/localization';

import { Button, TextBox } from 'devextreme-react';

import MessageForm, { MessageFormHandle, ErrorMessage } from '../../components/message-form/MessageForm';
import PathApi from '../../api/PathApi';
import PathVersionStatus from '../../components/path-version-status/PathVersionStatus';
import ChangeInProgressForm from '../../components/change-in-progress-form/ChangeInProgressForm';

import SourceSelection from '../../components/source-selection/SourceSelection';
import FiltersSelection from '../../components/filters-selection/FiltersSelection';
import useFilteringCapabilities from '../../components/filters-selection/useFilteringCapabilities';
import { collectDeclaredParameters } from '../../components/filter-selection/FilterBuilderInline';

import ReportingExposedFields, { ReportingExposedFieldsHandle } from '../../components/reporting-exposed-fields/ReportingExposedFields';
import ReportingResume from '../../components/reporting-resume/ReportingResume';

import PathReportingApi from '../../api/PathReportingApi';

import GetPathReportingDetailResult from '../../classes/api/result/pathreportingmodule/GetPathReportingDetailResult';
import PathReportingMappingDto from '../../classes/dtos/pathreportingmodule/PathReportingMappingDto';

import DataTableReadDto from '../../classes/dtos/pathcommonmodule/DataTableReadDto';
import DataSourceDataTableReadDto from '../../classes/dtos/pathcommonmodule/DataSourceDataTableReadDto';
import PathDataTableReadDto from '../../classes/dtos/pathcommonmodule/PathDataTableReadDto';

import ReportingFieldMappingDto from '../../classes/dtos/pathreportingmodule/ReportingFieldMappingDto';

import PathCheckResultDto from '../../classes/dtos/pathmodule/PathCheckResultDto';
import { checkPathBeforeSaveAndPublish, getPathPublishChecklist, PathBeforeSaveValidationInput, ValidationRow } from '../../logics/pathmodule/PathBeforeSaveValidation';
import { performPathSave } from '../../logics/pathmodule/performPathSave';
import { useAuth } from '../../contexts/auth';

import TransformationCapabilitiesDto from '../../classes/dtos/transformationmodule/TransformationCapabilitiesDto';
import TransformationNotConfigurableDto from '../../classes/dtos/transformationmodule/TransformationNotConfigurableDto';
import PathReportingMappingCapabilitiesDto from '../../classes/dtos/pathreportingmodule/PathReportingMappingCapabilitiesDto';

import DataTypeDto from '../../classes/dtos/datastructuremodule/DataTypeDto';

import FieldResumeDto from '../../classes/dtos/pathcommonmodule/FieldResumeDto';


export default function PathReporting() {

    const { id } = useParams();

    const { sessionDetail } = useAuth();

    const navigate = useNavigate();

    const [searchParams, setSearchParams] = useSearchParams();

    const pathId = Number.parseInt(id ?? "0");

    // Feature flag BD (Feature.FilterVisibility) : première condition d'affichage de l'onglet "Filters".
    const filterTabsEnabled = sessionDetail?.WithFilterVisibility ?? false;

    const messageFormHandleRef = useRef<MessageFormHandle>(null);

    const reportingExposedFieldsRef = useRef<ReportingExposedFieldsHandle>(null);

    const [pathReportingDetail, setPathReportingDetail] = useState<GetPathReportingDetailResult | null>(null);

    // "Advanced mode" checkbox (FILTER column header). Page-level state, persisted on the mapping version.
    const [advancedFilterMode, setAdvancedFilterMode] = useState<boolean>(false);
    // Advanced mode is a one-way switch: once it has been saved it can't be turned back off.
    const [advancedFilterModeLocked, setAdvancedFilterModeLocked] = useState<boolean>(false);

    // L'onglet "Filters" (mode complexe) n'est disponible que si la feature BD est active ET que le mode avancé
    // est activé sur ce path — fusion du feature flag Feature.FilterVisibility et de l'ancien `!simpleFilterMode`.
    const filtersTabAvailable = filterTabsEnabled && advancedFilterMode;

    // Colonne FILTER des grilles : visible seulement si la table source gère le filtrage. Même calcul
    // (`withFilter`) que l'onglet "Filters" dans FiltersSelection, via le hook partagé.
    const filterSourceTableReferenceId = pathReportingDetail?.Source instanceof DataSourceDataTableReadDto ? (pathReportingDetail.Source.TableReferenceId ?? 0) : 0;
    const { withFilter: sourceHandlesFilters } = useFilteringCapabilities(filterSourceTableReferenceId);

    const [capabilities, setCapabilities] = useState<PathReportingMappingCapabilitiesDto[]>([]);
    const [transformationsCapabilities, setTransformationsCapabilities] = useState<TransformationCapabilitiesDto[]>([]);

    const [mapping, setMapping] = useState<ReportingFieldMappingDto[]>([]);

    const MAX_TAB = 3;
    const FILTERS_TAB = 2;

    const [currentTab, setCurrentTab] = useState<number>(() => {
        const tabFromUrl = Number(searchParams.get('tab'));
        const isValid = !Number.isNaN(tabFromUrl) && tabFromUrl >= 0 && tabFromUrl <= MAX_TAB && (filterTabsEnabled || tabFromUrl !== FILTERS_TAB);
        return isValid ? tabFromUrl : 0;
    });

    const [isOpen, setIsOpen] = useState<boolean>(false);

    const changeInProgress = useRef<boolean>(false);

    // Mirror of changeInProgress for rendering (the ref alone doesn't trigger a re-render).
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
    const [isPublished, setIsPublished] = useState<boolean>(false);

    const markChanged = useCallback(() => {
        changeInProgress.current = true;
        setHasUnsavedChanges(true);
    }, []);

    const [editNameInprogress, setEditNameInprogress] = useState<string | null>(null);

    const dataChangesRef = useRef<DataChange[]>([]);

    const selectedIdsRef = useRef<number[]>([]);

    const pathReportingMappingDtosRef = useRef<PathReportingMappingDto[]>([]);

    const [toastConfig, setToastConfig] = useState({
        isVisible: false,
        type: 'info',
        message: '',
        withRedirect: false
    } as {
        isVisible: boolean,
        type: 'info' | 'error' | 'success',
        message: string,
        withRedirect: boolean
    });

    useEffect(() => {
        PathReportingApi.GetPathReportingDetailAsync(pathId)
            .then(detail => {
                if (detail.IsFailed()) {
                    navigate("/paths");
                }
                else {
                    setPathReportingDetail(detail.Result as GetPathReportingDetailResult);
                    setAdvancedFilterMode((detail.Result as GetPathReportingDetailResult).AdvancedFilterMode);
                    setIsPublished((detail.Result as GetPathReportingDetailResult).PublishedVersionId !== null && (detail.Result as GetPathReportingDetailResult).PublishedVersionId === (detail.Result as GetPathReportingDetailResult).LastVersionId);
                    setAdvancedFilterModeLocked((detail.Result as GetPathReportingDetailResult).AdvancedFilterMode);
                }
            });
    }, [pathId, navigate]);

    // Identité de la table source (indépendante de Parameters.Filter/Definitions) : ne doit changer que si la table
    // elle-même change, pas quand seul le filtre est modifié, sous peine de perdre la sélection de champs en cours.
    function getSourceTableKey(source: DataTableReadDto | null): string | null {
        if (source instanceof DataSourceDataTableReadDto) {
            return `ds:${source.DataSourceId}:${source.TableReferenceId}`;
        }
        if (source instanceof PathDataTableReadDto) {
            return `path:${source.PathId}`;
        }
        return null;
    }

    const sourceTableKey = getSourceTableKey(pathReportingDetail?.Source ?? null);

    const dataLoad = useCallback(async () => {

        if (pathReportingDetail === null) {
            return;
        }

        if (pathReportingDetail.Source === null) {
            return;
        }

        let tableReferenceId: number | null = null;
        let pathId: number | null = null;

        if (pathReportingDetail.Source instanceof DataSourceDataTableReadDto) {
            if ((pathReportingDetail.Source.DataSourceId ?? 0) > 0 && (pathReportingDetail.Source.TableReferenceId ?? 0) > 0) {
                tableReferenceId = pathReportingDetail.Source.TableReferenceId;
            }
        }

        if (pathReportingDetail.Source instanceof PathDataTableReadDto) {
            if ((pathReportingDetail.Source.PathId ?? 0) > 0) {
                pathId = pathReportingDetail.Source.PathId;
            }
        }

        if (tableReferenceId !== null || pathId !== null) {

            let getPathReportingCapabilitiesResult = await PathReportingApi.GetPathReportingCapabilitiesAsync(tableReferenceId, pathId);

            if (getPathReportingCapabilitiesResult.IsFailed()) {
                alert("Error : " + getPathReportingCapabilitiesResult.ErrorMessage);
                return;
            }

            let allFieldsSource = getPathReportingCapabilitiesResult.Result.SourceFields
                .toSorted((a, b) => (a.Name.toLowerCase() > b.Name.toLowerCase()) ? 1 : (a.Name.toLowerCase() < b.Name.toLowerCase()) ? -1 : 0);

            setCapabilities(getPathReportingCapabilitiesResult.Result.MappingsCapabilities);
            let transformationsCapabilitiesTemp = getPathReportingCapabilitiesResult.Result.TransformationsCapabilities;
            transformationsCapabilitiesTemp.push(new TransformationNotConfigurableDto(0, "Direct", 0, new DataTypeDto(0, false, ""), new DataTypeDto(0, false, ""), "TransformationNotConfigurable"));
            setTransformationsCapabilities(transformationsCapabilitiesTemp);

            let Mappings = pathReportingMappingDtosRef.current.length > 0
                ? pathReportingMappingDtosRef.current.map(e => new PathReportingMappingDto(e.FieldReferenceId, e.TransformationLogicId, e.TransformationParameters, e.DisplayName, selectedIdsRef.current.includes(e.FieldReferenceId), e.Filters, e.DeclaredVariables))
                : (pathReportingDetail?.Mappings ?? []);

            let mappingTemp = allFieldsSource.map((e) => {
                let readMapping = Mappings?.find(x => x.FieldReferenceId === e.Id) ?? null;
                if (readMapping === null) {
                    return new ReportingFieldMappingDto(e.Id, null, null, e.Name, e.Type, e.Name, []);
                } else {

                    let readDisplayName = readMapping.DisplayName;
                    if (readDisplayName === null || readDisplayName === undefined || readDisplayName === "")
                    {
                        readDisplayName = e.Name;
                    }

                    return new ReportingFieldMappingDto(e.Id, readMapping.TransformationLogicId, readMapping.TransformationParameters, e.Name, e.Type, readDisplayName, readMapping.Filters, readMapping.DeclaredVariables);
                }
            })

            selectedIdsRef.current = Mappings.filter(e => e.IsSelected).map(e => e.FieldReferenceId);

            setMapping(mappingTemp);

            let result = new Array<PathReportingMappingDto>();

            mappingTemp.forEach((e: ReportingFieldMappingDto) => {
                let readMapping = Mappings.find(x => x.FieldReferenceId === e.SourceFieldReferenceId) ?? null;
                result.push(new PathReportingMappingDto(e.SourceFieldReferenceId ?? 0, e.TransformationType ?? 0, e.TransformationParameters ?? "", e.DisplayName ?? "", readMapping?.IsSelected ?? false, e.Filters, e.DeclaredVariables));
            });

            pathReportingMappingDtosRef.current = result;
        } else {
            setCapabilities([]);
            setTransformationsCapabilities([]);
            setMapping([]);
        }
    }, [sourceTableKey]);

    useEffect(() => {
        dataLoad()
    }, [dataLoad]);

    // The name is saved on its own (not part of the version): it never creates a version nor counts as an unsaved change.
    async function handleNameChange() {
        const newName = editNameInprogress;
        if (newName === null) {
            return;
        }

        const renameResult = await PathApi.RenamePathAsync(pathId, newName);
        if (renameResult.IsFailed()) {
            messageFormHandleRef.current?.Open(formatMessage("PathValidation_SaveFailed"), ErrorMessage);
            return;
        }

        setPathReportingDetail((previous) => {
            if (previous === null) {
                return previous;
            }
            previous.Name = newName;
            return previous;
        });
        setEditNameInprogress(null);
        setToastConfig({
            ...toastConfig,
            isVisible: true,
            type: 'success',
            message: formatMessage('PathName_Saved'),
            withRedirect: false
        });
    }

    const handleDataChanges = useCallback((changes: DataChange[], origin: ReportingFieldMappingDto[]) => {
        dataChangesRef.current = changes;

        // Grid edits are unsaved changes too (the save buttons depend on it).
        if (changes.length > 0) {
            markChanged();
        }

        let result = new Array<PathReportingMappingDto>();

        origin.forEach((e: ReportingFieldMappingDto) => {
            let existing = pathReportingMappingDtosRef.current.find(x => x.FieldReferenceId === e.SourceFieldReferenceId) ?? null;
            result.push(new PathReportingMappingDto(e.SourceFieldReferenceId, e.TransformationType ?? 0, e.TransformationParameters ?? "", e.DisplayName ?? "", existing?.IsSelected ?? false, e.Filters, e.DeclaredVariables));
        });

        changes.forEach((e: DataChange) => {

            let row = result.find((x) => x.FieldReferenceId === e.key) ?? null;

            if (row !== null) {
                if (e.data.SourceFieldReferenceId !== undefined) {
                    row.FieldReferenceId = e.data.SourceFieldReferenceId;
                }
                if (e.data.TransformationType !== undefined) {
                    row.TransformationLogicId = e.data.TransformationType ?? 0;
                }
                if (e.data.TransformationParameters !== undefined) {
                    row.TransformationParameters = e.data.TransformationParameters;
                }
                if (e.data.DisplayName !== undefined) {
                    row.DisplayName = e.data.DisplayName;
                }
                if (e.data.Filters !== undefined) {
                    row.Filters = e.data.Filters;
                }
                if (e.data.DeclaredVariables !== undefined) {
                    row.DeclaredVariables = e.data.DeclaredVariables;
                }
            }
        });

        pathReportingMappingDtosRef.current = result;

    }, []);

    const handleSelectionChanges = useCallback((ids: number[]) => {
        selectedIdsRef.current = ids;
        markChanged();
    }, []);

    const handleAdvancedFilterModeChange = useCallback((value: boolean) => {
        if (advancedFilterModeLocked && !value) {
            return;
        }
        setAdvancedFilterMode(value);
        markChanged();
    }, [advancedFilterModeLocked]);

    const handleDataSourceRefreshed = useCallback(() => {
        dataLoad();
    }, [dataLoad]);

    const handleTableMappingChange = useCallback((tableMappingReadDto: DataTableReadDto) => {
        setPathReportingDetail((previous) => {
            if (previous === null) {
                return previous;
            }

            markChanged();

            return new GetPathReportingDetailResult(
                previous.Id,
                previous.Name,
                tableMappingReadDto,
                previous.Mappings,
                previous.Schedule,
                previous.LastVersionId,
                previous.PublishedVersionId,
                previous.AdvancedFilterMode);
        });

    }, []);

    function GetPathReportingMappingCapabilities(fieldId: number) {
        return capabilities.find((e) => e.SourceFieldId === fieldId) ?? null;
    }

    function GetFieldName(fieldId: number) {
        return mapping.find((e) => e.SourceFieldReferenceId === fieldId)?.Name ?? "";
    }

    function buildPathValidationInput(): PathBeforeSaveValidationInput {
        const selectedRows: ValidationRow[] = pathReportingMappingDtosRef.current
            .filter((e) => selectedIdsRef.current.includes(e.FieldReferenceId))
            .map((e) => ({
                labelFieldId: e.FieldReferenceId,
                transformationLogicId: e.TransformationLogicId,
                transformationParameters: e.TransformationParameters,
                fieldName: e.DisplayName,
            }));

        return {
            name: pathReportingDetail?.Name,
            source: pathReportingDetail?.Source,
            selectedRows,
            fieldNameMode: 'free',
            transformationsCapabilities,
            getAllowedTransformationLogicIds: (row) =>
                GetPathReportingMappingCapabilities(row.labelFieldId)?.TransformationLogicIds ?? null,
            getFieldLabel: (fieldId) => GetFieldName(fieldId),
        };
    }

    function CheckBeforeSaveAndPublish(): PathCheckResultDto {
        return checkPathBeforeSaveAndPublish(buildPathValidationInput());
    }

    async function save(publish: boolean): Promise<boolean> {
        const detail = pathReportingDetail;
        if (detail === null || detail.Source === null || messageFormHandleRef.current === null) {
            messageFormHandleRef.current?.Open(formatMessage("PathValidation_TechnicalError"), ErrorMessage);
            return false;
        }
        const source = detail.Source;

        const allMappings = pathReportingMappingDtosRef.current.map(e => new PathReportingMappingDto(e.FieldReferenceId, e.TransformationLogicId, e.TransformationParameters, e.DisplayName, selectedIdsRef.current.includes(e.FieldReferenceId), e.Filters, e.DeclaredVariables));

        return performPathSave({
            messageForm: messageFormHandleRef.current,
            checkResult: CheckBeforeSaveAndPublish(),
            checklist: getPathPublishChecklist(buildPathValidationInput()),
            publish,
            persist: () => PathReportingApi.UpdatePathReportingDetailAsync(pathId, detail.Name, allMappings, source, detail.Schedule, advancedFilterMode, publish),
            onSuccess: (isPublished) => {
                reportingExposedFieldsRef.current?.Save();
                if (advancedFilterMode) {
                    setAdvancedFilterModeLocked(true);
                }
                changeInProgress.current = false;
                setHasUnsavedChanges(false);
                setIsPublished(isPublished);
                setToastConfig({
                    ...toastConfig,
                    isVisible: true,
                    type: 'success',
                    message: publish ? 'Path saved and published' : 'Path saved as draft',
                    withRedirect: false
                });
            },
        });
    }

    const onHiding = useCallback(() => {
        setToastConfig({
            ...toastConfig,
            isVisible: false,
        });

        if (toastConfig.withRedirect) {
            navigate("/paths");
        }

    }, [toastConfig, navigate]);

    function cancel(): void {

        if (changeInProgress.current) {
            setIsOpen(true);
        }
        else {
            navigate("/paths");
        }
    }

    function cancelFromModal(): void {

        setIsOpen(false);
        navigate("/paths");
    }

    async function saveDraftFromModal(): Promise<void> {

        setIsOpen(false);
        if (await save(false)) {
            navigate("/paths");
        }
    }


    function changePage(index: number): void {
        // Onglet "Filters" indisponible (feature off ou mode avancé désactivé) : on l'enjambe dans le sens de navigation.
        const target = !filtersTabAvailable && index === FILTERS_TAB
            ? (index > currentTab ? index + 1 : index - 1)
            : index;
        setCurrentTab(target);
        setSearchParams({ tab: target.toString() }, { replace: true });
    };

    function back() {
        changePage(currentTab - 1);
    }

    function next() {
        changePage(currentTab + 1);
    }

    if (pathReportingDetail === null) {
        return (<div>Waiting</div>);
    }

    function GetFieldsResume(): FieldResumeDto[] {
        let fieldsResume = new Array<FieldResumeDto>();
        pathReportingMappingDtosRef.current.forEach((e) => {
            fieldsResume.push(new FieldResumeDto(
                e.FieldReferenceId,
                GetFieldName(e.FieldReferenceId),
                e.DisplayName));
        });
        return fieldsResume;
    }

    // "Advanced mode" (Enterprise only) drives the Filters tab; the per-row FILTER column is always shown.
    const enterpriseMode = sessionDetail?.EnterpriseMode ?? false;

    return (
        <React.Fragment>
            <div style={{ width: '100%', height: '100%', padding: 24, flexDirection: 'column', justifyContent: 'space-between', alignItems: 'flex-start', display: 'inline-flex' }}>
                <div style={{ alignSelf: 'stretch', height: '100%', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 24, display: 'flex' }}>
                    <div style={{ alignSelf: 'stretch', justifyContent: 'space-between', alignItems: 'center', display: 'inline-flex', minHeight: '49px' }}>
                        {editNameInprogress === null ?
                            <div style={{ justifyContent: 'flex-start', alignItems: 'center', gap: 20, display: 'flex' }}>
                                <div style={{ color: '#E3E3E8', fontSize: 24, fontFamily: 'Manrope', fontWeight: '600', lineHeight: '36px', letterSpacing: 0.48, wordWrap: 'break-word' }}>
                                    {pathReportingDetail?.Name ?? "???"}
                                </div>
                                <img src="svgs/edit.svg" alt="circle" style={{ cursor: 'pointer' }} onClick={() => setEditNameInprogress(pathReportingDetail?.Name ?? "???")} />
                            </div> :
                            <div style={{ width: '50%', justifyContent: 'flex-start', alignItems: 'center', gap: 10, display: 'flex' }}>
                                <TextBox style={{ width: '50%' }} defaultValue={pathReportingDetail?.Name ?? "???"} onValueChange={(e) => setEditNameInprogress(e)} />
                                <Button icon="check" onClick={() => handleNameChange()} />
                                <Button icon="close" onClick={() => setEditNameInprogress(null)} />
                            </div>}
                        <Button text="Close" type="normal" stylingMode="text" onClick={() => cancel()} />
                    </div>
                    <Tabs width='100%' stylingMode='secondary' onSelectedIndexChange={changePage} selectedIndex={currentTab} >
                        <Item text="Source">
                        </Item>
                        <Item text="Exposed field(s)">
                        </Item>
                        <Item text="Filters" visible={filtersTabAvailable} disabled={pathReportingDetail.Source instanceof PathDataTableReadDto}>
                        </Item>
                        <Item text="Checking">
                        </Item>
                    </Tabs>
                    {(currentTab === 0) ? <SourceSelection OriginTableId={null} OriginPathId={null} Direction="Source" WithDataTable={true} TableMapping={pathReportingDetail.Source ?? null} SetTableMapping={handleTableMappingChange} DataSourceRefreshed={handleDataSourceRefreshed} /> : ""}
                    {(currentTab === 1) ? <ReportingExposedFields
                        ref={reportingExposedFieldsRef}
                        MappingChanges={dataChangesRef.current}
                        SetMappingChanges={handleDataChanges}
                        Capabilities={capabilities}
                        TransformationsCapabilities={transformationsCapabilities}
                        Mapping={mapping}
                        IsSimpleFilterMode={enterpriseMode}
                        FiltersEnabled={filterTabsEnabled && sourceHandlesFilters}
                        TableReferenceId={(pathReportingDetail.Source as DataSourceDataTableReadDto)?.TableReferenceId ?? 0}
                        AdvancedFilterMode={advancedFilterMode}
                        SetAdvancedFilterMode={handleAdvancedFilterModeChange}
                        AdvancedFilterModeLocked={advancedFilterModeLocked}
                        SelectionIds={selectedIdsRef.current}
                        SetSelectionIds={handleSelectionChanges} /> : ""}
                    {(currentTab === 2 && filtersTabAvailable) ? <FiltersSelection TableMapping={pathReportingDetail.Source ?? null} SetTableMapping={handleTableMappingChange} WithSimpleParameters={true} DeclaredParameters={collectDeclaredParameters(pathReportingMappingDtosRef.current)} SelectedFieldReferenceIds={selectedIdsRef.current} /> : ""}
                    {(currentTab === 3) ? <ReportingResume PathId={pathId} Source={pathReportingDetail.Source ?? null} Mapping={GetFieldsResume()} SelectionIds={selectedIdsRef.current ?? []} /> : ""}
                </div>
                <div style={{ width: '100%', height: 64, position: 'absolute', bottom: 32, left: 0, display: 'flex', justifyContent: 'space-between', padding: '16px 24px' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Button icon='back' text="Back to previous step" type="normal" stylingMode="text" onClick={() => back()} disabled={currentTab === 0} />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                        <PathVersionStatus HasUnsavedChanges={hasUnsavedChanges} IsPublished={isPublished} />
                        <Button text="Save as draft" type="normal" stylingMode="text" onClick={() => save(false)} disabled={!hasUnsavedChanges} />
                        {currentTab === MAX_TAB ?
                            <Button text="Save and publish" type="default" stylingMode="contained" onClick={() => save(true)} disabled={isPublished && !hasUnsavedChanges} /> :
                            <Button text="Continue to next step" type="default" stylingMode="contained" onClick={() => next()} />}
                    </div>
                </div>
            </div>
            <ChangeInProgressForm IsOpen={isOpen} SetIsOpen={setIsOpen} Ok={() => saveDraftFromModal()} Cancel={() => cancelFromModal()} />
            <MessageForm ref={messageFormHandleRef} />
            <Toast
                visible={toastConfig.isVisible}
                message={toastConfig.message}
                type={toastConfig.type}
                onHiding={onHiding}
                displayTime={1000}
            />
        </React.Fragment>
    )
}
